import React, { useState, useRef, useContext, useCallback, useEffect, useMemo } from "react";
import { decode } from "@googlemaps/polyline-codec";
import { useTranslation } from "react-i18next";
import GoogleMapReact from "google-map-react";
import RoomIcon from "@mui/icons-material/Room";
import SchoolIcon from "@mui/icons-material/School";

import Marker from "shared/components/UIElements/Marker";
import Button from "shared/components/FormElements/Button";
import GenericModal from "components/shared/GenericModal";
import { AuthContext } from "shared/context/auth-context";
import { useBackend } from "shared/hooks/backend-hook";
import { useUsageGuard } from "shared/hooks/useUsageGuard";

import "./Map.css";

// Bundled (multi-school) route map — a SINGLE ordered chain of stops where each stop is either a
// STUDENT (home) or a SCHOOL (institution). Unlike MapWithTransferTimes there is NO per-stop manual
// time entry: the operator sets ONE anchor time and every other stop is derived from the real
// leg travel durations (the same Directions calls that draw the polyline).
//
//   PICK_UP  → anchor = arrival time at the LAST school; earlier stops computed BACKWARD.
//   DROP_OFF → anchor = departure time from the FIRST school; later stops computed FORWARD.
//
// stops: [{ key, kind: "STUDENT"|"SCHOOL", refId, name, location:{lat,lng}, time }] (ordered)

const isValidStopLocation = (location) => {
  if (!location) return false;
  const lat = Number(location.lat);
  const lng = Number(location.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === -1.1 && lng === -1.1) return false;
  if (lat === 0 && lng === 0) return false;
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
};

const isValidTimeString = (value) => typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value.trim());

const parseHHmmToSeconds = (value) => {
  const [h, m] = value.trim().split(":").map((x) => parseInt(x, 10));
  return h * 3600 + m * 60;
};

const secondsToHHmm = (seconds) => {
  const totalMin = Math.round(seconds / 60);
  const norm = ((totalMin % 1440) + 1440) % 1440; // wrap into a single 24h day
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// The anchor is the reference stop whose time the operator types: last school (PICK_UP) so we can
// walk arrival times backward, first school (DROP_OFF) so we can walk departure times forward.
const pickAnchorStop = (orderedStops, isPickup) => {
  const schools = orderedStops.filter((s) => s.kind === "SCHOOL");
  if (isPickup) return schools.length ? schools[schools.length - 1] : orderedStops[orderedStops.length - 1];
  return schools.length ? schools[0] : orderedStops[0];
};

const MapWithTransferTimesForBundled = ({
  stops,
  isPickup = true,
  anchorTime = "",
  onAnchorTimeChange,
  onReorder,
  onComputedTimes,
  onOptimize,
  isOptimizing = false,
  savedTotalDistanceKm,
  savedTotalDurationMin,
  onMetrics,
}) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const { getDirectionSingle } = useBackend();
  const { checkUsageForMapsDirectionsSingle, UsageGuardModal } = useUsageGuard();

  const mapREF = useRef(null);
  const mapsREF = useRef(null);
  const polylinesREF = useRef([]);
  const initialFitDone = useRef(false);
  const currentVersion = useRef(0);
  // Cumulative travel seconds from the first stop to each stop (keyed by stop.key). Populated by
  // drawPolylines and consumed by the time-compute effect — kept in a ref so recomputing times on
  // an anchor change never re-triggers the (usage-billed) Directions fetch.
  const cumulativeREF = useRef(new Map());

  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [cumulativeVersion, setCumulativeVersion] = useState(0);
  const [totalDistance, setTotalDistance] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [metricsDirty, setMetricsDirty] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [mapCenter] = useState({ lat: 39.925533, lng: 32.866287 });
  const [mapZoom] = useState(11);

  const validStops = useMemo(() => (stops || []).filter((s) => isValidStopLocation(s?.location)), [stops]);

  // Signature of ORDER + LOCATIONS only (NOT times). Directions are refetched only when this
  // changes, so typing an anchor time recomputes stop times without hitting the Directions API.
  const geometrySignature = useMemo(
    () => validStops.map((s) => `${s.key}@${s.location.lat},${s.location.lng}`).join("|"),
    [validStops],
  );

  const anchorKey = useMemo(() => {
    const anchor = pickAnchorStop(validStops, isPickup);
    return anchor ? anchor.key : null;
  }, [validStops, isPickup]);

  const useSavedMetrics = savedTotalDistanceKm !== undefined || savedTotalDurationMin !== undefined;
  const showLiveMetrics = metricsDirty || !useSavedMetrics;

  const handleApiLoaded = (map, maps) => {
    mapREF.current = map;
    mapsREF.current = maps;
    setMapLoaded(true);
  };

  const fitBoundsToStops = useCallback(() => {
    if (!mapREF.current || !mapsREF.current || validStops.length === 0) return;
    const bounds = new mapsREF.current.LatLngBounds();
    validStops.forEach((s) => bounds.extend(new mapsREF.current.LatLng(s.location.lat, s.location.lng)));
    mapREF.current.fitBounds(bounds);
  }, [validStops]);

  const drawPolylines = useCallback(async () => {
    if (!mapREF.current || !mapsREF.current) return;

    const version = ++currentVersion.current;
    setLoading(true);

    for (let k = 0; k < polylinesREF.current.length; k++) {
      polylinesREF.current[k].setMap(null);
    }
    polylinesREF.current = [];

    if (validStops.length < 2) {
      if (!initialFitDone.current) {
        fitBoundsToStops();
        initialFitDone.current = true;
      }
      cumulativeREF.current = new Map(validStops.map((s) => [s.key, 0]));
      setCumulativeVersion((v) => v + 1);
      setTotalDistance(0);
      setTotalDuration(0);
      setLoading(false);
      return;
    }

    const legs = [];
    for (let i = 0; i < validStops.length - 1; i++) {
      legs.push({ origin: validStops[i], destination: validStops[i + 1] });
    }

    const usageCheck = await checkUsageForMapsDirectionsSingle(legs.length);
    if (!usageCheck.allowed) {
      if (!initialFitDone.current) {
        fitBoundsToStops();
        initialFitDone.current = true;
      }
      setLoading(false);
      return;
    }

    const directions = await Promise.all(
      legs.map(async (leg) => {
        const details = await getDirectionSingle(leg.origin, leg.destination, auth.token);
        return details;
      }),
    );

    if (currentVersion.current !== version) {
      setLoading(false);
      return;
    }

    let missing = false;
    for (let i = 0; i < directions.length; i++) {
      if (!directions[i]?.overview_polyline) missing = true;
    }
    if (missing) {
      setModalMessage(t("directionError"));
      setShowModal(true);
    }

    let distance = 0;
    let duration = 0;
    const newPolylines = [];
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;

    // Cumulative travel offset (seconds) from the first stop to each stop.
    const cumulative = new Map();
    cumulative.set(validStops[0].key, 0);
    let runningSeconds = 0;

    for (let i = 0; i < directions.length; i++) {
      const d = directions[i];
      runningSeconds += Number(d?.duration) || 0;
      cumulative.set(validStops[i + 1].key, runningSeconds);

      if (!d?.overview_polyline?.points) continue;
      distance += Number(d.distance) || 0;
      duration += Number(d.duration) || 0;

      const path = decode(d.overview_polyline.points).map((c) => ({ lat: c[0], lng: c[1] }));
      // eslint-disable-next-line no-loop-func
      path.forEach(({ lat, lng }) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      });

      const polyline = new mapsREF.current.Polyline({
        path,
        strokeColor: "#FF0000",
        strokeOpacity: 1,
        strokeWeight: 2,
        icons: [{ icon: { path: mapsREF.current.SymbolPath.FORWARD_CLOSED_ARROW }, offset: "100%", repeat: "40px" }],
      });
      polyline.setMap(mapREF.current);
      newPolylines.push(polyline);
    }

    polylinesREF.current = newPolylines;
    cumulativeREF.current = cumulative;
    setCumulativeVersion((v) => v + 1);

    if (!initialFitDone.current && minLat !== Infinity) {
      const bounds = new mapsREF.current.LatLngBounds();
      bounds.extend(new mapsREF.current.LatLng(maxLat, maxLng));
      bounds.extend(new mapsREF.current.LatLng(minLat, minLng));
      mapREF.current.fitBounds(bounds);
      initialFitDone.current = true;
    }

    setTotalDistance(distance);
    setTotalDuration(duration);
    setLoading(false);

    if (onMetrics) {
      onMetrics({
        distanceKm: Math.round((distance / 1000) * 10) / 10,
        durationMin: Math.max(0, Math.round(duration / 60)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validStops, auth.token, t, fitBoundsToStops]);

  // Fetch directions / draw polylines only when the ORDER or LOCATIONS change (not on time edits).
  useEffect(() => {
    if (!mapLoaded) return;
    drawPolylines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometrySignature, mapLoaded]);

  // Derive every stop time from the single anchor time + the real cumulative travel offsets.
  const liftComputedTimes = useCallback(() => {
    if (!onComputedTimes) return;
    if (!isValidTimeString(anchorTime)) return;
    const cumulative = cumulativeREF.current;
    if (!cumulative || cumulative.size === 0) return;

    const anchorStop = pickAnchorStop(validStops, isPickup);
    if (!anchorStop) return;
    const anchorOffset = cumulative.get(anchorStop.key) || 0;
    const anchorAbsolute = parseHHmmToSeconds(anchorTime);

    const entries = validStops
      .filter((s) => cumulative.has(s.key))
      .map((s) => {
        const delta = (cumulative.get(s.key) || 0) - anchorOffset;
        return { key: s.key, time: secondsToHHmm(anchorAbsolute + delta) };
      });

    if (entries.length > 0) onComputedTimes(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorTime, isPickup, validStops, onComputedTimes]);

  useEffect(() => {
    liftComputedTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cumulativeVersion, anchorTime, isPickup, geometrySignature]);

  const moveStop = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= stops.length) return;
    const next = [...stops];
    [next[index], next[target]] = [next[target], next[index]];
    setMetricsDirty(true);
    onReorder(next);
  };

  const handleOptimizeClick = async () => {
    setMetricsDirty(true);
    await onOptimize();
  };

  const distanceLabel = showLiveMetrics
    ? `${(totalDistance / 1000).toFixed(1)} ${t("km")}`
    : Number(savedTotalDistanceKm) > 0
      ? `${Number(savedTotalDistanceKm).toFixed(1)} ${t("km")}`
      : "-";
  const durationLabel = showLiveMetrics
    ? `${(totalDuration / 60).toFixed(0)} ${t("minutes")}`
    : Number(savedTotalDurationMin) > 0
      ? `${Math.round(Number(savedTotalDurationMin))} ${t("minutes")}`
      : "-";

  const anchorLabel = isPickup ? t("bundledRoutes.arrivalAtSchool") : t("bundledRoutes.departureFromSchool");

  return (
    <React.Fragment>
      <GenericModal message={modalMessage} title={t("directionError")} onClose={() => setShowModal(false)} showModal={showModal} />
      <UsageGuardModal />

      <div style={{ width: "400px" }}>
        <div style={{ display: "flex", columnGap: "20px", marginTop: "10px", marginBottom: "10px" }}>
          <div>
            <span>{t("distance")}: </span>
            <b id="totalDistance">{loading ? "..." : distanceLabel}</b>
          </div>
          <div>
            <span>{t("duration")}: </span>
            <b id="totalDuration">{loading ? "..." : durationLabel}</b>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#f4f6fb",
            border: "1px solid #d7deec",
            borderRadius: "6px",
            padding: "10px",
            marginBottom: "10px",
          }}
        >
          <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>{anchorLabel}</label>
          <input
            type="text"
            placeholder="HH:mm"
            style={{ height: "32px", width: "90px", paddingLeft: "6px", borderColor: "black" }}
            value={anchorTime || ""}
            onInput={(e) => onAnchorTimeChange && onAnchorTimeChange(e.target.value)}
          />
          <div style={{ fontSize: "12px", color: "#5a6474", marginTop: "6px" }}>{t("bundledRoutes.anchorHint")}</div>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <Button type="button" danger disabled={loading || isOptimizing || validStops.length < 2} onClick={handleOptimizeClick}>
            {isOptimizing ? t("bundledRoutes.optimizing") : t("optimize")}
          </Button>
        </div>

        {stops.map((stop, index) => {
          const isAnchor = stop.key === anchorKey;
          return (
            <div
              key={stop.key}
              style={{ display: "flex", alignItems: "center", width: "100%", marginBottom: "6px" }}
            >
              <div style={{ display: "flex", flexDirection: "column", marginRight: "6px" }}>
                <button type="button" style={{ backgroundColor: "black", padding: "4px", marginBottom: "3px" }} onClick={() => moveStop(index, -1)}>
                  <div className="arrow-up"></div>
                </button>
                <button type="button" style={{ backgroundColor: "black", padding: "4px" }} onClick={() => moveStop(index, 1)}>
                  <div className="arrow-down"></div>
                </button>
              </div>
              <div
                style={{
                  backgroundColor: stop.kind === "SCHOOL" ? "#1d3469" : "#bbb",
                  color: stop.kind === "SCHOOL" ? "#fff" : "#000",
                  padding: "10px",
                  borderRadius: "5px",
                  flex: 1,
                  fontWeight: stop.kind === "SCHOOL" ? 600 : 400,
                  outline: isAnchor ? "2px solid #f0a500" : "none",
                }}
              >
                {index + 1 + ". " + (stop.kind === "SCHOOL" ? "🏫 " : "") + (stop.name || "")}
              </div>
              <input
                type="text"
                readOnly
                placeholder="--:--"
                title={isAnchor ? anchorLabel : undefined}
                style={{
                  height: "30px",
                  width: "60px",
                  marginLeft: "5px",
                  paddingLeft: "5px",
                  borderColor: isAnchor ? "#f0a500" : "black",
                  backgroundColor: "#f2f2f2",
                }}
                value={stop.time || ""}
              />
            </div>
          );
        })}
      </div>

      <div className="shared-service-map-container-big">
        <GoogleMapReact
          bootstrapURLKeys={{ language: "en", region: "us", libraries: ["drawing", "geometry", "encoding"] }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
          defaultCenter={mapCenter}
          defaultZoom={mapZoom}
        >
          {validStops.map((stop) => {
            const index = stops.findIndex((s) => s.key === stop.key);
            return (
              <Marker
                key={stop.key}
                Icon={stop.kind === "SCHOOL" ? SchoolIcon : RoomIcon}
                lat={stop.location.lat}
                lng={stop.location.lng}
                info={index + 1 + ". " + (stop.name || "") + (stop.time ? " - " + stop.time : "")}
                initialShow
                alwaysShowMarker
                alwaysBlue={stop.kind !== "SCHOOL"}
              />
            );
          })}
        </GoogleMapReact>
      </div>
    </React.Fragment>
  );
};

export default MapWithTransferTimesForBundled;
